import { getOrderDeliveryStatusLabel } from '../../../../../utils/order/orderDeliveryStatusDisplay';
import { getOrderListPaymentStatusLabel } from '../../../../../utils/order/orderListStatusFilterOptions';
import { ORDER_APPROVAL_STATUS_LABELS } from '../../../../../utils/order/orderStatus';
import { getOrderPrimaryPhaseLabel, getOrderStatisticsPrimaryPhase, getStatisticsLensData, ORDER_PRIMARY_PHASE_META } from '../../pipeline/lib/orderPrimaryPhaseConfig';
import {
	ORDER_PRIMARY_PHASE_DIMENSIONS,
	ORDER_PRIMARY_PHASE_LINE_ITEM_DIMENSIONS,
	resolveOrderDimensionKey,
	resolveOrderDimensionLabel,
	resolveOrderLineItemDimensionBuckets,
} from '../../pipeline/lib/orderPrimaryPhaseDimension';
import { buildOrderCityBuckets, normalizeProvinceMapName } from '../../pipeline/lib/orderPrimaryPhaseGeoBuckets';
import { getPrimaryPhaseRowMetricValue } from '../../pipeline/lib/orderPrimaryPhaseMetric';
import {
	accumulatePrimaryPhaseOrderFinancials,
	accumulatePrimaryPhaseOrderFinancialsPartial,
	createEmptyPrimaryPhaseTotals,
} from '../../pipeline/lib/primaryPhaseOrderFinancials';
import { buildStatisticsMaterialScopedOrder } from '../../../filters/lib/statisticsMaterialLineContribution';
import {
	COMPOSITION_CHART_DIMENSION,
	COMPOSITION_CHART_LINE_ITEM_DIMENSIONS,
	COMPOSITION_CHART_METRIC,
	getCompositionChartFixedSliceOrder,
	isCompositionChartFixedSliceDimension,
	isCompositionChartMapType,
	normalizeCompositionChartMetrics,
	resolvePrimaryCompositionChartMetric,
} from './compositionChartConfig';

const HIGH_CARDINALITY_DIMENSIONS = new Set([
	COMPOSITION_CHART_DIMENSION.SALE_USER,
	COMPOSITION_CHART_DIMENSION.ASSISTANT_USER,
	COMPOSITION_CHART_DIMENSION.CUSTOMER,
	COMPOSITION_CHART_DIMENSION.CUSTOMER_CONTACT,
	COMPOSITION_CHART_DIMENSION.BRAND,
	COMPOSITION_CHART_DIMENSION.SUPPLIER,
	COMPOSITION_CHART_DIMENSION.CATEGORY,
	COMPOSITION_CHART_DIMENSION.PRODUCT_NAME,
	COMPOSITION_CHART_DIMENSION.PRODUCT_CODE,
]);
const MAX_SLICES = 10;

const PRIMARY_PHASE_SORT_ORDER = Object.fromEntries(ORDER_PRIMARY_PHASE_META.map(({ phase }, index) => [phase, index]));

const COMPOSITION_TO_ORDER_DIMENSION = {
	[COMPOSITION_CHART_DIMENSION.SUPPLIER]: ORDER_PRIMARY_PHASE_DIMENSIONS.SUPPLIER,
	[COMPOSITION_CHART_DIMENSION.CATEGORY]: ORDER_PRIMARY_PHASE_DIMENSIONS.CATEGORY,
	[COMPOSITION_CHART_DIMENSION.PRODUCT_NAME]: ORDER_PRIMARY_PHASE_DIMENSIONS.PRODUCT_NAME,
	[COMPOSITION_CHART_DIMENSION.PRODUCT_CODE]: ORDER_PRIMARY_PHASE_DIMENSIONS.PRODUCT_CODE,
	[COMPOSITION_CHART_DIMENSION.BRAND]: ORDER_PRIMARY_PHASE_DIMENSIONS.BRAND,
};

const CHART_AGGREGATE_DIMENSION_KEYS = {
	[COMPOSITION_CHART_DIMENSION.SALE_USER]: 'saleUser',
	[COMPOSITION_CHART_DIMENSION.ASSISTANT_USER]: 'assistantUser',
	[COMPOSITION_CHART_DIMENSION.CUSTOMER]: 'customer',
	[COMPOSITION_CHART_DIMENSION.CUSTOMER_CONTACT]: 'customerContact',
	[COMPOSITION_CHART_DIMENSION.PROVINCE]: 'province',
	[COMPOSITION_CHART_DIMENSION.BRAND]: 'brand',
	[COMPOSITION_CHART_DIMENSION.SUPPLIER]: 'supplier',
	[COMPOSITION_CHART_DIMENSION.CATEGORY]: 'category',
	[COMPOSITION_CHART_DIMENSION.PRODUCT_NAME]: 'productName',
	[COMPOSITION_CHART_DIMENSION.PRODUCT_CODE]: 'productCode',
};

/** 与主阶段/关单口径一致：待核验不算已完成 */
const resolveOrderChartCompletedStatus = (order) => {
	if (order?.uiHints?.isPendingFinalCostConfirmation || getOrderStatisticsPrimaryPhase(order) === 'pendingReview') {
		return false;
	}
	return Boolean(order?.isCompleted) || Boolean(order?.isFinalCostConfirmed);
};

export const resolveCompositionChartDimensionBucket = (order, dimension) => {
	if (dimension === COMPOSITION_CHART_DIMENSION.IS_COMPLETED) {
		const completed = resolveOrderChartCompletedStatus(order);
		return {
			key: completed ? 'completed' : 'notCompleted',
			label: completed ? '已完成' : '未完成',
		};
	}

	if (dimension === COMPOSITION_CHART_DIMENSION.PRIMARY_PHASE) {
		const phase = getOrderStatisticsPrimaryPhase(order) ?? '';
		return {
			key: phase || '__unknown__',
			label: phase ? getOrderPrimaryPhaseLabel(phase) : '未知',
		};
	}

	if (dimension === COMPOSITION_CHART_DIMENSION.APPROVAL_STATUS) {
		const key = order?.approvalStatus ?? '';
		return { key: key || '__unknown__', label: ORDER_APPROVAL_STATUS_LABELS[key] ?? key ?? '未知' };
	}

	if (dimension === COMPOSITION_CHART_DIMENSION.PAYMENT_STATUS) {
		const key = order?.paymentStatus ?? '';
		return { key: key || '__unknown__', label: getOrderListPaymentStatusLabel(key) || key || '未知' };
	}

	if (dimension === COMPOSITION_CHART_DIMENSION.DELIVERY_STATUS) {
		const key = order?.deliveryStatus ?? '';
		return { key: key || '__unknown__', label: getOrderDeliveryStatusLabel(key) || key || '未知' };
	}

	if (dimension === COMPOSITION_CHART_DIMENSION.PAYMENT_METHOD) {
		return {
			key: resolveOrderDimensionKey(order, ORDER_PRIMARY_PHASE_DIMENSIONS.PAYMENT_METHOD),
			label: resolveOrderDimensionLabel(order, ORDER_PRIMARY_PHASE_DIMENSIONS.PAYMENT_METHOD),
		};
	}

	if (dimension === COMPOSITION_CHART_DIMENSION.SALE_USER) {
		return {
			key: resolveOrderDimensionKey(order, ORDER_PRIMARY_PHASE_DIMENSIONS.SALE_USER),
			label: resolveOrderDimensionLabel(order, ORDER_PRIMARY_PHASE_DIMENSIONS.SALE_USER),
		};
	}

	if (dimension === COMPOSITION_CHART_DIMENSION.ASSISTANT_USER) {
		return {
			key: resolveOrderDimensionKey(order, ORDER_PRIMARY_PHASE_DIMENSIONS.ASSISTANT_USER),
			label: resolveOrderDimensionLabel(order, ORDER_PRIMARY_PHASE_DIMENSIONS.ASSISTANT_USER),
		};
	}

	if (dimension === COMPOSITION_CHART_DIMENSION.CUSTOMER) {
		return {
			key: resolveOrderDimensionKey(order, ORDER_PRIMARY_PHASE_DIMENSIONS.CUSTOMER),
			label: resolveOrderDimensionLabel(order, ORDER_PRIMARY_PHASE_DIMENSIONS.CUSTOMER),
		};
	}

	if (dimension === COMPOSITION_CHART_DIMENSION.CUSTOMER_CONTACT) {
		return {
			key: resolveOrderDimensionKey(order, ORDER_PRIMARY_PHASE_DIMENSIONS.CUSTOMER_CONTACT),
			label: resolveOrderDimensionLabel(order, ORDER_PRIMARY_PHASE_DIMENSIONS.CUSTOMER_CONTACT),
		};
	}

	if (dimension === COMPOSITION_CHART_DIMENSION.PROVINCE) {
		return {
			key: resolveOrderDimensionKey(order, ORDER_PRIMARY_PHASE_DIMENSIONS.PROVINCE),
			label: resolveOrderDimensionLabel(order, ORDER_PRIMARY_PHASE_DIMENSIONS.PROVINCE),
		};
	}

	if (dimension === COMPOSITION_CHART_DIMENSION.BRAND) {
		return {
			key: resolveOrderDimensionKey(order, ORDER_PRIMARY_PHASE_DIMENSIONS.BRAND),
			label: resolveOrderDimensionLabel(order, ORDER_PRIMARY_PHASE_DIMENSIONS.BRAND),
		};
	}

	return { key: '__unknown__', label: '未知' };
};

const getRowMetricValue = (row, metric) => getPrimaryPhaseRowMetricValue(row, metric);

/** 行维度扇区单数可交叉；合计用去重订单数，才能和走势图单数对齐 */
const resolveCompositionUniqueOrderCount = ({ primaryPhaseStats, orders = [], selectedPhase = null }) => {
	const lensData = getStatisticsLensData(primaryPhaseStats);
	if (selectedPhase) {
		const phaseRow = (lensData?.phases ?? []).find((row) => (row.phase ?? row.key) === selectedPhase);
		const phaseCount = Number(phaseRow?.count);
		if (Number.isFinite(phaseCount)) return phaseCount;
		return (orders ?? []).filter((order) => getOrderStatisticsPrimaryPhase(order) === selectedPhase).length;
	}
	const overviewCount = Number(lensData?.overview?.count);
	if (Number.isFinite(overviewCount)) return overviewCount;
	return (orders ?? []).length;
};

const resolveCompositionTotalCount = ({ items, dimension, uniqueOrderCount }) => {
	const summed = (items ?? []).reduce((sum, row) => sum + (Number(row.count) || 0), 0);
	if (!COMPOSITION_CHART_LINE_ITEM_DIMENSIONS.has(dimension) || uniqueOrderCount == null || !Number.isFinite(Number(uniqueOrderCount))) {
		return summed;
	}
	return Number(uniqueOrderCount);
};

const createEmptyCompositionChartBucket = (key, label) => ({
	key,
	label,
	...createEmptyPrimaryPhaseTotals(),
});

export const filterOrdersForCompositionChart = (orders = [], { selectedPhase } = {}) =>
	(orders ?? []).filter((order) => !selectedPhase || getOrderStatisticsPrimaryPhase(order) === selectedPhase);

export const buildCompositionChartProvinceMapRows = (orders = [], metric = COMPOSITION_CHART_METRIC.COUNT, filters = {}) => {
	const buckets = new Map();

	for (const order of orders ?? []) {
		const statsOrder = buildStatisticsMaterialScopedOrder(order, filters);
		const { key, label } = resolveCompositionChartDimensionBucket(statsOrder, COMPOSITION_CHART_DIMENSION.PROVINCE);
		if (!key || key === 'province:__unknown__' || !label || label === '未知地区') continue;
		const mapName = normalizeProvinceMapName(label);
		if (!mapName) continue;
		const prev = buckets.get(mapName) ?? createEmptyCompositionChartBucket(mapName, mapName);
		prev.name = mapName;
		accumulatePrimaryPhaseOrderFinancials(prev, statsOrder);
		buckets.set(mapName, prev);
	}

	return [...buckets.values()]
		.filter((row) => getRowMetricValue(row, metric) > 0)
		.sort((a, b) => getRowMetricValue(b, metric) - getRowMetricValue(a, metric) || a.name.localeCompare(b.name, 'zh-CN'));
};

const collapseHighCardinalityRows = (items, metric) => {
	if (items.length <= MAX_SLICES) return items;

	const sorted = [...items].sort((a, b) => getRowMetricValue(b, metric) - getRowMetricValue(a, metric));
	const head = sorted.slice(0, MAX_SLICES - 1);
	const tail = sorted.slice(MAX_SLICES - 1);
	const other = tail.reduce(
		(acc, row) => {
			const next = { ...acc };
			next.count += row.count;
			next.totalPrice += row.totalPrice;
			next.receivableTotal += row.receivableTotal;
			next.quasiReceivableTotal += row.quasiReceivableTotal;
			next.pendingPrepaymentTotal += row.pendingPrepaymentTotal;
			next.paidPrepaymentTotal += row.paidPrepaymentTotal;
			return next;
		},
		createEmptyCompositionChartBucket('__other__', '其他'),
	);
	other.otherKeys = tail.map((row) => row.key).filter(Boolean);

	return getRowMetricValue(other, metric) > 0 ? [...head, other] : head;
};

const accumulateLineItemCompositionOrder = (buckets, order, dimension) => {
	const orderDimension = COMPOSITION_TO_ORDER_DIMENSION[dimension];
	if (!orderDimension || !ORDER_PRIMARY_PHASE_LINE_ITEM_DIMENSIONS.has(orderDimension)) return;

	const lineBuckets = resolveOrderLineItemDimensionBuckets(order, orderDimension);
	const countedKeys = new Set();

	for (const line of lineBuckets) {
		const prev = buckets.get(line.key) ?? createEmptyCompositionChartBucket(line.key, line.label);
		prev.label = line.label;
		const incrementCount = !countedKeys.has(line.key);
		if (incrementCount) countedKeys.add(line.key);
		accumulatePrimaryPhaseOrderFinancialsPartial(prev, order, {
			amount: line.amount,
			incrementCount,
		});
		buckets.set(line.key, prev);
	}
};

/** 按维度聚合订单，返回图表行 */
export const buildCompositionChartRows = ({
	orders = [],
	dimension = COMPOSITION_CHART_DIMENSION.IS_COMPLETED,
	metric = COMPOSITION_CHART_METRIC.COUNT,
	metrics = null,
	selectedPhase = null,
	filters = {},
}) => {
	const activeMetrics = normalizeCompositionChartMetrics(metrics ?? metric);
	const primaryMetric = resolvePrimaryCompositionChartMetric(activeMetrics);
	const buckets = new Map();
	const isLineItemDimension = COMPOSITION_CHART_LINE_ITEM_DIMENSIONS.has(dimension);
	let uniqueOrderCount = 0;

	for (const order of orders ?? []) {
		if (selectedPhase && getOrderStatisticsPrimaryPhase(order) !== selectedPhase) continue;
		uniqueOrderCount += 1;
		const statsOrder = buildStatisticsMaterialScopedOrder(order, filters);

		if (isLineItemDimension) {
			accumulateLineItemCompositionOrder(buckets, statsOrder, dimension);
			continue;
		}

		const { key, label } = resolveCompositionChartDimensionBucket(statsOrder, dimension);
		const prev = buckets.get(key) ?? createEmptyCompositionChartBucket(key, label);
		prev.label = label;
		accumulatePrimaryPhaseOrderFinancials(prev, statsOrder);
		buckets.set(key, prev);
	}

	let items = [...buckets.values()];

	if (isCompositionChartFixedSliceDimension(dimension)) {
		for (const slot of getCompositionChartFixedSliceOrder(dimension)) {
			if (!buckets.has(slot.key)) {
				buckets.set(slot.key, createEmptyCompositionChartBucket(slot.key, slot.label));
			}
		}
		items = getCompositionChartFixedSliceOrder(dimension).map((slot) => buckets.get(slot.key));
	} else if (dimension === COMPOSITION_CHART_DIMENSION.PRIMARY_PHASE) {
		items.sort(
			(a, b) =>
				(PRIMARY_PHASE_SORT_ORDER[a.key] ?? 999) - (PRIMARY_PHASE_SORT_ORDER[b.key] ?? 999) ||
				a.label.localeCompare(b.label, 'zh-CN'),
		);
	} else {
		items.sort(
			(a, b) =>
				getRowMetricValue(b, primaryMetric) - getRowMetricValue(a, primaryMetric) ||
				a.label.localeCompare(b.label, 'zh-CN'),
		);
	}

	if (HIGH_CARDINALITY_DIMENSIONS.has(dimension)) {
		items = collapseHighCardinalityRows(items, primaryMetric);
	}

	const totalCount = resolveCompositionTotalCount({
		items,
		dimension,
		uniqueOrderCount: isLineItemDimension ? uniqueOrderCount : null,
	});
	const totalMetric = items.reduce((sum, row) => sum + getRowMetricValue(row, primaryMetric), 0);

	return { items, totalCount, totalMetric, metrics: activeMetrics, primaryMetric };
};

const finalizeCompositionItems = (rawItems, dimension, primaryMetric, activeMetrics, uniqueOrderCount = null) => {
	let items = [...(rawItems ?? [])];

	if (isCompositionChartFixedSliceDimension(dimension)) {
		const byKey = new Map(items.map((row) => [row.key, row]));
		for (const slot of getCompositionChartFixedSliceOrder(dimension)) {
			if (!byKey.has(slot.key)) {
				byKey.set(slot.key, createEmptyCompositionChartBucket(slot.key, slot.label));
			}
		}
		items = getCompositionChartFixedSliceOrder(dimension).map((slot) => byKey.get(slot.key));
	} else if (dimension === COMPOSITION_CHART_DIMENSION.PRIMARY_PHASE) {
		items.sort(
			(a, b) =>
				(PRIMARY_PHASE_SORT_ORDER[a.key] ?? 999) - (PRIMARY_PHASE_SORT_ORDER[b.key] ?? 999) ||
				a.label.localeCompare(b.label, 'zh-CN'),
		);
	} else {
		items.sort(
			(a, b) =>
				getRowMetricValue(b, primaryMetric) - getRowMetricValue(a, primaryMetric) ||
				a.label.localeCompare(b.label, 'zh-CN'),
		);
	}

	if (HIGH_CARDINALITY_DIMENSIONS.has(dimension)) {
		items = collapseHighCardinalityRows(items, primaryMetric);
	}

	const totalCount = resolveCompositionTotalCount({ items, dimension, uniqueOrderCount });
	const totalMetric = items.reduce((sum, row) => sum + getRowMetricValue(row, primaryMetric), 0);
	return { items, totalCount, totalMetric, metrics: activeMetrics, primaryMetric };
};

const mapStatsRowsToCompositionItems = (rows = []) =>
	(rows ?? []).map((row) => ({
		...createEmptyCompositionChartBucket(row.key, row.label || '未分类'),
		count: Number(row.count) || 0,
		totalPrice: Number(row.totalPrice) || 0,
		trueProfitTotal: Number(row.trueProfitTotal) || 0,
		receivableTotal: Number(row.receivableTotal) || 0,
		quasiReceivableTotal: Number(row.quasiReceivableTotal) || 0,
		pendingPrepaymentTotal: Number(row.pendingPrepaymentTotal) || 0,
		paidPrepaymentTotal: Number(row.paidPrepaymentTotal) || 0,
		personId: row.personId || '',
	}));

/**
 * 无全量 orders 时用服务端 chartAggregates / lenses / axisStatusCounts 组装组成图。
 * 有 selectedPhase 时聚合未按节点切片，回退订单扫表。
 */
export const buildCompositionChartRowsFromStats = ({
	primaryPhaseStats = null,
	orders = null,
	dimension = COMPOSITION_CHART_DIMENSION.IS_COMPLETED,
	metric = COMPOSITION_CHART_METRIC.COUNT,
	metrics = null,
	selectedPhase = null,
	filters = {},
} = {}) => {
	const orderList = orders ?? primaryPhaseStats?.orders ?? [];
	const activeMetrics = normalizeCompositionChartMetrics(metrics ?? metric);
	const primaryMetric = resolvePrimaryCompositionChartMetric(activeMetrics);
	const uniqueOrderCount = resolveCompositionUniqueOrderCount({
		primaryPhaseStats,
		orders: orderList,
		selectedPhase,
	});

	/** 选了流程节点时服务端维度聚合未切片，只能扫订单 */
	if (selectedPhase) {
		return buildCompositionChartRows({
			orders: orderList,
			dimension,
			metric,
			metrics,
			selectedPhase,
			filters,
		});
	}

	const lensData = getStatisticsLensData(primaryPhaseStats);
	if (dimension === COMPOSITION_CHART_DIMENSION.PRIMARY_PHASE && lensData?.phases) {
		const phases = lensData.phases;
		const items = phases.map((row) => ({
			...createEmptyCompositionChartBucket(row.phase, getOrderPrimaryPhaseLabel(row.phase)),
			count: Number(row.count) || 0,
			totalPrice: Number(row.totalPrice) || 0,
			trueProfitTotal: Number(row.trueProfitTotal) || 0,
			receivableTotal: Number(row.receivableTotal) || 0,
			quasiReceivableTotal: Number(row.quasiReceivableTotal) || 0,
			pendingPrepaymentTotal: Number(row.pendingPrepaymentTotal) || 0,
			paidPrepaymentTotal: Number(row.paidPrepaymentTotal) || 0,
		}));
		return finalizeCompositionItems(items, dimension, primaryMetric, activeMetrics, uniqueOrderCount);
	}

	if (dimension === COMPOSITION_CHART_DIMENSION.PAYMENT_STATUS && primaryPhaseStats?.axisStatusCounts?.payment) {
		const payment = primaryPhaseStats.axisStatusCounts.payment;
		const items = Object.entries(payment).map(([key, bucket]) => ({
			...createEmptyCompositionChartBucket(key, getOrderListPaymentStatusLabel(key) || key || '未知'),
			count: Number(bucket?.count) || 0,
			totalPrice: Number(bucket?.totalPrice) || 0,
		}));
		return finalizeCompositionItems(items, dimension, primaryMetric, activeMetrics, uniqueOrderCount);
	}

	if (dimension === COMPOSITION_CHART_DIMENSION.DELIVERY_STATUS && primaryPhaseStats?.axisStatusCounts?.delivery) {
		const delivery = primaryPhaseStats.axisStatusCounts.delivery;
		const items = Object.entries(delivery).map(([key, bucket]) => ({
			...createEmptyCompositionChartBucket(key, getOrderDeliveryStatusLabel(key) || key || '未知'),
			count: Number(bucket?.count) || 0,
			totalPrice: Number(bucket?.totalPrice) || 0,
		}));
		return finalizeCompositionItems(items, dimension, primaryMetric, activeMetrics, uniqueOrderCount);
	}

	const aggregateKey = CHART_AGGREGATE_DIMENSION_KEYS[dimension];
	if (aggregateKey && Array.isArray(primaryPhaseStats?.chartAggregates?.dimensions?.[aggregateKey])) {
		const rows = primaryPhaseStats.chartAggregates.dimensions[aggregateKey];
		return finalizeCompositionItems(mapStatsRowsToCompositionItems(rows), dimension, primaryMetric, activeMetrics, uniqueOrderCount);
	}

	return buildCompositionChartRows({
		orders: orderList,
		dimension,
		metric,
		metrics,
		selectedPhase,
		filters,
	});
};

export const hasCompositionChartData = (orders = [], config, context = {}) => {
	const normalizedMetrics = normalizeCompositionChartMetrics(config?.metrics ?? config?.metric);
	const primaryMetric = resolvePrimaryCompositionChartMetric(normalizedMetrics);

	if (isCompositionChartMapType(config?.chartType)) {
		if (context?.primaryPhaseStats?.chartAggregates?.geo?.cityBuckets?.length) {
			return true;
		}
		const filteredOrders = filterOrdersForCompositionChart(orders, { selectedPhase: context.selectedPhase });
		if (config?.dimension === COMPOSITION_CHART_DIMENSION.CUSTOMER) {
			return buildOrderCityBuckets(filteredOrders).rows.length > 0;
		}
		if (config?.dimension === COMPOSITION_CHART_DIMENSION.PROVINCE) {
			return buildCompositionChartProvinceMapRows(filteredOrders, primaryMetric).length > 0;
		}
		return false;
	}

	const { items } = buildCompositionChartRowsFromStats({
		primaryPhaseStats: context?.primaryPhaseStats,
		orders,
		dimension: config?.dimension,
		metrics: normalizedMetrics,
		selectedPhase: context.selectedPhase,
	});
	return items.some((row) => normalizedMetrics.some((metric) => getRowMetricValue(row, metric) > 0));
};
